import type { ReactNode, Ref } from 'react';
import './AdminPage.css';

/**
 * The page shell every system admin page uses. The admin layout's container
 * (`.dashboard-section-container`) has no padding on purpose, so the page brings its own: one
 * padding, one max width and one gap between sections, so no two admin pages drift apart again.
 *
 * `width="full"` is for pages whose main content is a wide table or a two-pane review screen.
 */
interface AdminPageProps {
  children: ReactNode;
  width?: 'default' | 'full';
  /** The id of the page title, for `aria-labelledby`. */
  labelledBy?: string;
  className?: string;
  'data-testid'?: string;
}

export const AdminPage = ({
  children,
  width = 'default',
  labelledBy,
  className,
  'data-testid': testId,
}: AdminPageProps) => (
  <section
    className={['admin-page', width === 'full' && 'admin-page--full', className].filter(Boolean).join(' ')}
    aria-labelledby={labelledBy}
    data-testid={testId}
  >
    {children}
  </section>
);

/**
 * The one page header: an optional back link above, the title (always an h2, since the dashboard
 * shell owns the h1), an optional subtitle, and actions on the right.
 */
interface AdminPageHeaderProps {
  title: ReactNode;
  titleId?: string;
  titleRef?: Ref<HTMLHeadingElement>;
  subtitle?: ReactNode;
  /** A back link, rendered above the title. */
  back?: ReactNode;
  /** Buttons, search and filters, right-aligned. Wraps under the title on narrow screens. */
  actions?: ReactNode;
  /** Status badges and the like, shown inline after the title. */
  meta?: ReactNode;
}

export const AdminPageHeader = ({ title, titleId, titleRef, subtitle, back, actions, meta }: AdminPageHeaderProps) => (
  <header className="admin-page__header">
    {back && <div className="admin-page__back">{back}</div>}
    <div className="admin-page__header-row">
      <div className="admin-page__heading">
        <div className="admin-page__title-row">
          <h2 className="admin-page__title" id={titleId} ref={titleRef} tabIndex={titleRef ? -1 : undefined}>
            {title}
          </h2>
          {meta}
        </div>
        {subtitle && <p className="admin-page__subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="admin-page__actions">{actions}</div>}
    </div>
  </header>
);

/**
 * A card that groups one part of a page. The title is an h3 under the page's h2.
 */
interface AdminPanelProps {
  children: ReactNode;
  title?: ReactNode;
  titleId?: string;
  titleRef?: Ref<HTMLHeadingElement>;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const AdminPanel = ({
  children,
  title,
  titleId,
  titleRef,
  description,
  actions,
  className,
  'data-testid': testId,
}: AdminPanelProps) => (
  <section
    className={['admin-panel', className].filter(Boolean).join(' ')}
    aria-labelledby={title && titleId ? titleId : undefined}
    data-testid={testId}
  >
    {(title || actions) && (
      <div className="admin-panel__header">
        <div className="admin-panel__heading">
          {title && (
            <h3 className="admin-panel__title" id={titleId} ref={titleRef} tabIndex={titleRef ? -1 : undefined}>
              {title}
            </h3>
          )}
          {description && <p className="admin-panel__description">{description}</p>}
        </div>
        {actions && <div className="admin-panel__actions">{actions}</div>}
      </div>
    )}
    {children}
  </section>
);
